import { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, LogOut, Download, Lock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (detail, fallback = 'Something went wrong.') => {
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
};

export default function Admin() {
  const [authed, setAuthed] = useState(null); // null=checking, false=login, true=in
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subs, setSubs] = useState([]);
  const [expanded, setExpanded] = useState({});

  const check = async () => {
    try {
      await axios.get(`${API}/admin/me`, { withCredentials: true });
      setAuthed(true);
    } catch {
      setAuthed(false);
    }
  };

  useEffect(() => { check(); }, []);

  const fetchSubs = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/submissions?limit=500`, { withCredentials: true });
      setSubs(data || []);
    } catch (e) {
      if (e?.response?.status === 401) setAuthed(false);
    }
  };

  useEffect(() => { if (authed === true) fetchSubs(); }, [authed]);

  const login = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/admin/login`, { password }, { withCredentials: true });
      setAuthed(true);
      setPassword('');
    } catch (e) {
      setError(errText(e?.response?.data?.detail, 'Invalid password.'));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/admin/logout`, {}, { withCredentials: true }); } catch { /* ignore */ }
    setAuthed(false);
    setSubs([]);
  };

  const exportCsv = () => {
    const headers = ['created_at', 'name', 'email', 'company', 'subject', 'message', 'email_status'];
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""').replace(/\n/g, ' ')}"`;
    const rows = [headers.join(','), ...subs.map((s) => headers.map((h) => escape(s[h])).join(','))];
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dasc-submissions-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (authed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5EFE1]">
        <Loader2 className="animate-spin text-[#8B6F47]" size={22} />
      </div>
    );
  }

  if (authed === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5EFE1] px-6" data-testid="admin-login-page">
        <form onSubmit={login} className="w-full max-w-sm bg-[#EBE3D0] border border-[#0B1F3F]/10 p-10">
          <Lock size={20} className="text-[#B58F45] mb-6" />
          <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-3">Admin</p>
          <h1 className="font-serif text-3xl text-[#0B1F3F] mb-8 leading-tight">Enter passphrase.</h1>
          <label className="block text-[11px] tracking-[0.2em] uppercase text-[#6B5B4A] mb-2">Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="admin-password-input"
            autoFocus
            className="w-full bg-transparent border-b border-[#0B1F3F]/25 py-3 text-[#0B1F3F] focus:outline-none focus:border-[#B58F45] transition-colors"
          />
          {error && <p className="mt-4 text-[13px] text-[#7A1E2E]" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading || !password}
            data-testid="admin-login-submit"
            className="mt-8 w-full py-3 bg-[#0B1F3F] text-[#F5EFE1] font-sans text-xs tracking-[0.25em] uppercase hover:bg-[#7A1E2E] disabled:opacity-50 transition-colors flex items-center justify-center gap-3"
          >
            {loading && <Loader2 className="animate-spin" size={14} />}
            Sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5EFE1] px-6 md:px-12 py-16" data-testid="admin-dashboard">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-end mb-12 flex-wrap gap-4">
          <div>
            <p className="text-[10px] tracking-[0.35em] uppercase text-[#B58F45] mb-3">Admin</p>
            <h1 className="font-serif text-4xl md:text-5xl text-[#0B1F3F]">Contact submissions</h1>
            <p className="text-[#6B5B4A] mt-2">{subs.length} total</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={exportCsv}
              disabled={!subs.length}
              data-testid="admin-export-csv"
              className="flex items-center gap-2 px-5 py-3 border border-[#0B1F3F]/25 text-[#0B1F3F] text-[11px] tracking-[0.2em] uppercase hover:bg-[#8B6F47] hover:text-[#F5EFE1] hover:border-[#8B6F47] transition-colors disabled:opacity-40"
            >
              <Download size={13} /> Export CSV
            </button>
            <button
              onClick={logout}
              data-testid="admin-logout"
              className="flex items-center gap-2 px-5 py-3 border border-[#0B1F3F]/25 text-[#0B1F3F] text-[11px] tracking-[0.2em] uppercase hover:bg-[#7A1E2E] hover:text-[#F5EFE1] hover:border-[#7A1E2E] transition-colors"
            >
              <LogOut size={13} /> Sign out
            </button>
          </div>
        </div>

        {subs.length === 0 ? (
          <div className="border border-[#0B1F3F]/10 bg-[#EBE3D0] p-12 text-center text-[#6B5B4A]">
            No submissions yet.
          </div>
        ) : (
          <div className="border border-[#0B1F3F]/10">
            {subs.map((s) => {
              const isOpen = !!expanded[s.id];
              const dateStr = new Date(s.created_at).toLocaleString();
              return (
                <div key={s.id} data-testid={`admin-submission-${s.id}`} className="border-b last:border-b-0 border-[#0B1F3F]/10 bg-[#EBE3D0]">
                  <button
                    onClick={() => setExpanded((x) => ({ ...x, [s.id]: !isOpen }))}
                    className="w-full text-left p-6 md:p-8 flex flex-wrap justify-between items-start gap-4 hover:bg-[#F5EFE1] transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] tracking-[0.25em] uppercase text-[#B58F45] mb-2">{dateStr}</p>
                      <p className="font-serif text-xl text-[#0B1F3F] leading-tight truncate">{s.subject}</p>
                      <p className="text-sm text-[#6B5B4A] mt-1 truncate">
                        {s.name} · {s.email}{s.company ? ` · ${s.company}` : ''}
                      </p>
                    </div>
                    <span className={`text-[10px] px-2 py-1 tracking-[0.2em] uppercase ${
                      s.email_status === 'sent' ? 'bg-[#8B6F47]/20 text-[#6B5B4A]' :
                      s.email_status === 'failed' ? 'bg-[#7A1E2E]/15 text-[#7A1E2E]' :
                      'bg-[#0B1F3F]/10 text-[#6B5B4A]'
                    }`}>{s.email_status}</span>
                  </button>
                  {isOpen && (
                    <div className="px-6 md:px-8 pb-8 -mt-2">
                      <div className="border-t border-[#0B1F3F]/10 pt-6 text-[#0B1F3F] whitespace-pre-wrap leading-relaxed">
                        {s.message}
                      </div>
                      {s.email_error && (
                        <p className="mt-4 text-[13px] text-[#7A1E2E]">Email error: {s.email_error}</p>
                      )}
                      <div className="mt-6 flex gap-4">
                        <a href={`mailto:${s.email}?subject=Re: ${encodeURIComponent(s.subject)}`}
                           className="text-[11px] tracking-[0.2em] uppercase text-[#0B1F3F] hover:text-[#B58F45] transition-colors">
                          Reply →
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
